import {
  Component,
  input,
  output,
  OnInit,
  signal,
  computed,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { QUESTION_TYPE_CONFIG } from '@hyper-inferno/skip-logic-core';
import { ConditionRulesManagerService } from './condition-rule-manager-service/condition-rules-manager.service';
import { SurveyBuilderService } from '../survey-builder/survey-builder.service';
import {
  LucideAngularModule,
  Plus,
  Trash2,
  GripVertical,
} from 'lucide-angular';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  ConditionSetElementType,
  Condition,
  ConditionSet,
  OPERATORS,
} from '@hyper-inferno/survey-elements-core';
@Component({
  selector: 'app-condition-rule-manager',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, LucideAngularModule],
  templateUrl: './condition-rule-manager.component.html',
  styleUrl: './condition-rule-manager.component.css',
})
export class ConditionRuleManagerComponent implements OnInit {
  // Signal Inputs
  parentId = input<any>();
  parentType = input.required<ConditionSetElementType>();
  conditionSetId = input<string | null>();
  conditionSetReady = output<string>();

  // Signal Outputs
  conditionsChanged = output<Condition[]>();

  // Services
  private conditionRulesManagerService = inject(ConditionRulesManagerService);
  private surveyBuilderService = inject(SurveyBuilderService);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);

  // State signals
  currentConditionSet = signal<ConditionSet>({
    id: '',
    skip_logic_id: undefined,
    logical_operator: 'AND',
    description: '',
    conditions: [],
    condition_set_element_id: undefined,
    condition_set_element_type: ConditionSetElementType.QUESTION_ELEMENT,
  });

  availableJumpTargets = signal<any[]>([]);
  isLoading = signal(false);
  isLoadingConditions = signal(false);
  isLoadingOptions = signal(false);
  selectedJumpTarget = signal<number | undefined>(undefined);

  // Question options map
  questionOptionsMap = new Map<number, any[]>();

  // Available questions for conditions
  availableQuestions = signal<any[]>([]);
  surveyId = signal<number | undefined>(undefined);

  // Icons
  readonly Plus = Plus;
  readonly Trash2 = Trash2;
  readonly GripVertical = GripVertical;

  // Computed
  hasConditions = computed(
    () => this.currentConditionSet().conditions.length > 0,
  );
  constructor() {
    this.route.parent?.params.subscribe((params) => {
      const id = params['id'];
      this.surveyId.set(id ? Number(id) : undefined);
    });
  }
  ngOnInit() {
    this.initializeConditionSet();
    this.loadAvailableQuestions();
  }

  async initializeOrFindConditionSet() {
    const elementId = this.parentId() as number;
    const parentType = this.parentType();

    try {
      let existingId = null;

      if (parentType === ConditionSetElementType.QUESTION_ELEMENT) {
        const result = await lastValueFrom(
          this.conditionRulesManagerService.getConditionSetByElementId(
            elementId,
          ),
        );
        existingId = result?.id;
      } else if (parentType === ConditionSetElementType.LOGIC_ELEMENT) {
        const result = await lastValueFrom(
          this.conditionRulesManagerService.getConditionSetBySkipLogicId(
            elementId.toString(),
          ),
        );
        existingId = result?.id;
      }

      if (existingId) {
        await this.loadExistingConditionSet(existingId);
      } else {
        await this.createNewConditionSet();
      }
    } catch (error) {
      console.error('Error initializing condition set:', error);
      await this.createNewConditionSet();
    }
  }

  async loadAvailableQuestions() {
    // Load questions that can be used in conditions
    const surveyId = this.surveyId();
    if (surveyId) {
      try {
        const elements = await lastValueFrom(
          this.surveyBuilderService.getElements(surveyId),
        );
        this.availableQuestions.set(
          elements.filter((el: any) => el.element_type === 'questionElement'),
        );
      } catch (error) {
        console.error('Error loading questions:', error);
      }
    }
  }

  async initializeConditionSet() {
    const existingId = this.conditionSetId();
    const parentId = this.parentId();
    const parentType = this.parentType();

    if (existingId) {
      // If conditionSetId was passed directly, use it
      await this.loadExistingConditionSet(existingId);
    } else if (parentId && parentType) {
      // Try to find existing condition set by element ID
      try {
        let foundConditionSet = null;

        if (parentType === 'questionElement') {
          foundConditionSet = await lastValueFrom(
            this.conditionRulesManagerService.getConditionSetByElementId(
              Number(parentId),
            ),
          );
        } else if (parentType === 'logicElement') {
          foundConditionSet = await lastValueFrom(
            this.conditionRulesManagerService.getConditionSetBySkipLogicId(
              parentId.toString(),
            ),
          );
        }

        if (foundConditionSet && foundConditionSet.id) {
          await this.loadExistingConditionSet(foundConditionSet.id);
        } else {
          // No existing condition set, creating new one
          await this.createNewConditionSet();
        }
      } catch (error) {
        console.log('Error finding condition set, creating new one...', error);
        await this.createNewConditionSet();
      }
    } else {
      console.log(' No parentId or parentType provided');
    }
  }

  async loadExistingConditionSet(conditionSetId: string) {
    this.isLoadingConditions.set(true);
    try {
      //  First load available questions if not already loaded
      if (this.availableQuestions().length === 0) {
        await this.loadAvailableQuestions();
      }

      const data = await lastValueFrom(
        this.conditionRulesManagerService.getConditionSet(conditionSetId),
      );

      this.currentConditionSet.set({
        id: data.id,
        skip_logic_id: data.skip_logic_id,
        logical_operator: data.logical_operator || 'AND',
        description: data.description || '',
        conditions: data.conditions || [],
        condition_set_element_id: data.condition_set_element_id,
        condition_set_element_type: data.condition_set_element_type,
      });

      //  Load options after conditions are set
      await this.loadOptionsForExistingConditions();
      this.conditionSetReady.emit(data.id);
    } catch (error) {
      console.error('Error loading condition set:', error);
    } finally {
      this.isLoadingConditions.set(false);
    }
  }

  isOptionSelected(condition: Condition, optionId: number | string): boolean {
    if (!condition.option_targets || condition.option_targets.length === 0) {
      return false;
    }

    const optionIdStr = String(optionId);
    const isSelected = condition.option_targets.some(
      (target) => String(target) === optionIdStr,
    );

    return isSelected;
  }
  async createNewConditionSet() {
    this.isLoading.set(true);

    const parentId = this.parentId();
    const parentType = this.parentType();

    const payload: any = {
      condition_set_element_type: parentType,
      logical_operator: 'AND',
    };

    if (parentType === 'logicElement') {
      payload.skip_logic_id = parentId;
    } else if (parentType === 'questionElement') {
      payload.condition_set_element_id = parentId;
    }

    try {
      const response = await lastValueFrom(
        this.conditionRulesManagerService.createConditionSet(payload),
      );

      this.currentConditionSet.update((cs) => ({
        ...cs,
        id: response.id,
        ...(parentType === 'logicElement'
          ? { skip_logic_id: parentId as string }
          : { condition_set_element_id: parentId as number }),
      }));

      // Emit that condition set is ready
      this.conditionSetReady.emit(response.id);
    } catch (error) {
      console.error('Error creating condition set:', error);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  async loadOptionsForExistingConditions() {
    for (const condition of this.currentConditionSet().conditions) {
      const question = this.getQuestionById(condition.condition_element_id);
      if (
        question &&
        (question.type === 'single-choice' ||
          question.type === 'multiple-choice')
      ) {
        if (!this.questionOptionsMap.has(question.id)) {
          try {
            const settings = await lastValueFrom(
              this.conditionRulesManagerService.getElementSettings(
                question.id,
                question.type,
              ),
            );

            if (settings && settings.list_id) {
              const options = await lastValueFrom(
                this.conditionRulesManagerService.getListOptions(
                  settings.list_id,
                ),
              );
              this.questionOptionsMap.set(question.id, options);
            } else {
              this.questionOptionsMap.set(question.id, []);
            }
          } catch (error) {
            console.error(
              `Error loading options for question ${question.id}:`,
              error,
            );
            this.questionOptionsMap.set(question.id, []);
          }
        }
      }
    }
  }

  addCondition() {
    const newCondition: Condition = {
      condition_element_id: 0,
      operator: '',
      option_targets: [],
      rank: this.currentConditionSet().conditions.length * 100,
    };

    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: [...cs.conditions, newCondition],
    }));
  }

  async removeCondition(conditionId?: string) {
    if (!conditionId) return;

    try {
      await lastValueFrom(
        this.conditionRulesManagerService.deleteCondition(conditionId),
      );

      this.currentConditionSet.update((cs) => ({
        ...cs,
        conditions: cs.conditions.filter((c) => c.id !== conditionId),
      }));

      this.conditionsChanged.emit(this.currentConditionSet().conditions);
    } catch (error) {
      console.error('Error deleting condition:', error);
    }
  }

  async onDescriptionChange(value: string) {
    this.currentConditionSet.update((cs) => ({
      ...cs,
      description: value,
    }));

    if (this.currentConditionSet().id) {
      try {
        await lastValueFrom(
          this.conditionRulesManagerService.updateConditionSet(
            this.currentConditionSet().id,
            {
              description: value,
            },
          ),
        );
      } catch (error) {
        console.error('Error saving description:', error);
      }
    }
  }

  async onLogicalOperatorChange(value: 'AND' | 'OR') {
    this.currentConditionSet.update((cs) => ({
      ...cs,
      logical_operator: value,
    }));

    if (this.currentConditionSet().id) {
      try {
        await lastValueFrom(
          this.conditionRulesManagerService.updateConditionSet(
            this.currentConditionSet().id,
            {
              logical_operator: value,
            },
          ),
        );
      } catch (error) {
        console.error('Error saving logical operator:', error);
      }
    }
  }

  async onConditionElementChange(condition: Condition) {
    if (typeof condition.condition_element_id === 'string') {
      condition.condition_element_id = parseInt(
        condition.condition_element_id,
        10,
      );
    }

    condition.operator = '';
    condition.option_id = undefined;
    condition.value_manual = undefined;
    condition.option_targets = [];

    const question = this.getQuestionById(condition.condition_element_id);
    if (
      question &&
      (question.type === 'single-choice' || question.type === 'multiple-choice')
    ) {
      if (!this.questionOptionsMap.has(question.id)) {
        this.isLoadingOptions.set(true);
        try {
          const settings = await lastValueFrom(
            this.conditionRulesManagerService.getElementSettings(
              question.id,
              question.type,
            ),
          );

          if (settings && settings.list_id) {
            const options = await lastValueFrom(
              this.conditionRulesManagerService.getListOptions(
                settings.list_id,
              ),
            );
            this.questionOptionsMap.set(question.id, options);
          } else {
            this.questionOptionsMap.set(question.id, []);
          }
        } catch (error) {
          console.error(`Error loading options:`, error);
          this.questionOptionsMap.set(question.id, []);
        } finally {
          this.isLoadingOptions.set(false);
        }
      }
    }

    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: [...cs.conditions],
    }));

    await this.onValueChange(condition);
  }

  async onOperatorChange(condition: Condition) {
    condition.option_id = undefined;
    condition.value_manual = undefined;
    condition.option_targets = [];

    if (condition.id && condition.operator) {
      await this.updateCondition(condition);
    }

    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: [...cs.conditions],
    }));
  }

  async onValueChange(condition: Condition) {
    const hasRequiredData =
      condition.condition_element_id &&
      condition.condition_element_id !== 0 &&
      condition.operator;

    if (hasRequiredData) {
      if (!condition.id) {
        await this.createCondition(condition);
      } else {
        await this.updateCondition(condition);
      }
    }
  }

  async createCondition(condition: Condition) {
    try {
      const payload = {
        skip_logic_id: this.currentConditionSet().skip_logic_id,
        condition_element_id: condition.condition_element_id,
        operator: condition.operator,
        option_id: condition.option_id,
        value_manual: condition.value_manual,
        option_targets: condition.option_targets,
        logical_operator: this.currentConditionSet().logical_operator,
        description: this.currentConditionSet().description,
        condition_set_element_id:
          this.currentConditionSet().condition_set_element_id,
        condition_set_element_type:
          this.currentConditionSet().condition_set_element_type,
      };

      const response = await lastValueFrom(
        this.conditionRulesManagerService.createCondition(payload),
      );

      condition.id = response.id;

      this.currentConditionSet.update((cs) => ({
        ...cs,
        conditions: cs.conditions.map((c) =>
          c === condition ? { ...c, id: response.id } : c,
        ),
      }));
    } catch (error) {
      console.error('Error creating condition:', error);
      throw error;
    }
  }

  async updateCondition(condition: Condition) {
    try {
      const payload = {
        condition_element_id: condition.condition_element_id,
        operator: condition.operator,
        option_id: condition.option_id,
        value_manual: condition.value_manual,
        option_targets: condition.option_targets,
        rank: condition.rank,
      };

      await lastValueFrom(
        this.conditionRulesManagerService.updateCondition(
          condition.id as string,
          payload,
        ),
      );
    } catch (error) {
      console.error('Error updating condition:', error);
      throw error;
    }
  }

  async onJumpTargetChange(targetId: number) {
    this.selectedJumpTarget.set(targetId);

    // Save to backend based on parent type
    const parentType = this.parentType();
    const parentId = this.parentId();

    if (parentType === 'logicElement' && parentId) {
      await lastValueFrom(
        this.conditionRulesManagerService.updateJumpToPage(
          parentId as string,
          targetId,
        ),
      );
    } else if (
      parentType === 'questionElement' &&
      this.currentConditionSet().id
    ) {
      // Save jump to element for question
      await lastValueFrom(
        this.conditionRulesManagerService.updateJumpToElement(
          this.currentConditionSet().id,
          targetId,
        ),
      );
    }
  }

  getQuestionById(questionId: number): any | undefined {
    return this.availableQuestions().find(
      (q) => Number(q.id) === Number(questionId),
    );
  }

  getQuestionOptions(questionId: number): any[] {
    return this.questionOptionsMap.get(questionId) || [];
  }

  getOperatorsForQuestion(questionId: number): any[] {
    const question = this.getQuestionById(questionId);
    if (!question) return [];

    const config = QUESTION_TYPE_CONFIG[question.type];
    if (!config) return [];

    return config.operators
      .map((opSlug: string) => OPERATORS[opSlug])
      .filter((op: any) => op !== undefined);
  }

  getOperatorArgType(operatorSlug: string): string {
    return OPERATORS[operatorSlug]?.argType || 'none';
  }

  getQuestionDisplayText(question: any): string {
    const truncateText = (html: string, limit = 15): string => {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      const plainText = (div.textContent || div.innerText || '')
        .replace(/\s+/g, ' ')
        .trim();
      return plainText.length > limit
        ? plainText.substring(0, limit) + '...'
        : plainText;
    };
    return `${question.name} ${truncateText(question.question_text, 15)}`;
  }

  toggleMultiOption(condition: Condition, optionId: string) {
    if (!condition.option_targets) {
      condition.option_targets = [];
    }

    const index = condition.option_targets.indexOf(optionId);
    if (index > -1) {
      condition.option_targets.splice(index, 1);
    } else {
      condition.option_targets.push(optionId);
    }

    this.onValueChange(condition);
  }

  drop(event: CdkDragDrop<Condition[]>) {
    const currentConditions = [...this.currentConditionSet().conditions];
    moveItemInArray(currentConditions, event.previousIndex, event.currentIndex);
    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: currentConditions,
    }));
    this.reorderConditions(event.previousIndex, event.currentIndex);
  }

  async reorderConditions(previousIndex: number, currentIndex: number) {
    if (previousIndex === currentIndex) return;

    try {
      const conditionSetId = this.currentConditionSet().id;
      if (!conditionSetId) return;

      const conditions = [...this.currentConditionSet().conditions];
      const MIN_GAP = 50;

      let newRank: number;
      if (currentIndex === 0) {
        newRank = Math.floor(conditions[1]?.rank / 2) || 50;
        newRank = Math.round(newRank / 50) * 50;
        if (newRank < 50) newRank = 50;
      } else if (currentIndex === conditions.length - 1) {
        const prevRank = conditions[conditions.length - 2]?.rank || 0;
        newRank = prevRank + 100;
      } else {
        const prevRank = conditions[currentIndex - 1].rank;
        const nextRank = conditions[currentIndex + 1].rank;
        newRank = Math.floor((prevRank + nextRank) / 2);
        newRank = Math.round(newRank / 50) * 50;
        if (newRank <= prevRank) newRank = prevRank + 50;
        if (newRank >= nextRank) newRank = nextRank - 50;
      }

      const updates = [
        {
          id: conditions[currentIndex].id as string,
          rank: newRank,
          condition_set_id: conditionSetId,
        },
      ];

      await lastValueFrom(
        this.conditionRulesManagerService.reorderConditions(updates),
      );

      this.currentConditionSet.update((cs) => ({
        ...cs,
        conditions: cs.conditions.map((c, idx) =>
          idx === currentIndex ? { ...c, rank: newRank } : c,
        ),
      }));

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to reorder conditions:', error);
    }
  }
}
